// src/components/features/ParkingSessions/ParkingSessionList.tsx
//
// Context support:
// - When rendered inside a <ParkingSessionsProvider>, data props (sessions,
//   loading, loadingMore, hasMore, onLoadMore, deletingSessionId) are read
//   from context automatically. Props can still override context values.
// - When rendered outside a provider, all data must be passed via props.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import {
  ParkingSession,
  PARKING_SESSION_STATUS,
} from "@/utils/api";
import { useOptionalParkingSessionsContext } from "@/contexts";
import {
  AppIcon,
  EmptyState,
  LoadMoreButton,
  useSwipeableList,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";

import {
  ParkingSessionCard,
  ParkingSessionCardSkeleton,
} from "./ParkingSessionCard";

// =============================================================================
// Types
// =============================================================================

export interface ParkingSessionListProps {
  /**
   * Array of parking session records to display.
   * Optional when inside a ParkingSessionsProvider — reads from context.
   */
  sessions?: ParkingSession[];

  /**
   * Loading state for initial load.
   * Optional when inside a ParkingSessionsProvider — reads from context.
   */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when edit action is clicked */
  onEditSession?: (session: ParkingSession) => void;

  /** Callback when delete action is clicked */
  onDeleteSession?: (session: ParkingSession) => void;

  /** Callback when end session is clicked */
  onEndSession?: (session: ParkingSession) => void;

  /** Callback when a session card is clicked (open detail) */
  onSessionClick?: (session: ParkingSession) => void;

  /** ID of session currently being deleted. Optional — reads from context. */
  deletingSessionId?: string | null;

  /** ID of session currently being ended */
  endingSessionId?: string | null;

  /** ID of recently created/edited session to highlight */
  highlightedSessionId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddSession?: () => void;

  /** Whether to group sessions (active pinned on top) (default: true) */
  grouped?: boolean;

  /** Currency formatting function */
  formatCurrency?: (amount: number, currency?: string) => string;
}

// =============================================================================
// Constants
// =============================================================================

/** Status group display order: active first, then completed */
const STATUS_GROUP_ORDER = [
  PARKING_SESSION_STATUS.ACTIVE,
  PARKING_SESSION_STATUS.COMPLETED,
];

/** Status group icons + colors for section headers */
const STATUS_GROUP_CONFIG: Record<number, { icon: string; color: string }> = {
  [PARKING_SESSION_STATUS.ACTIVE]: { icon: "Parking", color: "green" },
  [PARKING_SESSION_STATUS.COMPLETED]: { icon: "CircleCheck", color: "gray" },
};

// =============================================================================
// Loading Skeleton
// =============================================================================

const LoadingSkeleton: React.FC = () => (
  <Stack gap="md">
    {Array.from({ length: 5 }).map((_, i) => (
      <ParkingSessionCardSkeleton key={i} />
    ))}
  </Stack>
);

// =============================================================================
// Section Header
// =============================================================================

interface StatusSectionHeaderProps {
  status: number;
  count: number;
}

const StatusSectionHeaderComponent: React.FC<StatusSectionHeaderProps> = ({
  status,
  count,
}) => {
  const { t } = useTranslation(["parkingSessions"]);
  const config =
    STATUS_GROUP_CONFIG[status] ||
    STATUS_GROUP_CONFIG[PARKING_SESSION_STATUS.COMPLETED];

  const labelKey =
    status === PARKING_SESSION_STATUS.ACTIVE
      ? "parkingSessions:statusGroup.active"
      : "parkingSessions:statusGroup.completed";

  return (
    <Group gap={8} wrap="nowrap" py={4}>
      <ThemeIcon variant="light" color={config.color} size="xs" radius="xl">
        <AppIcon iconName={config.icon} size={12} />
      </ThemeIcon>
      <Text size="sm" fw={600} c={config.color} tt="uppercase" lts={0.5}>
        {t(labelKey, {
          defaultValue:
            status === PARKING_SESSION_STATUS.ACTIVE ? "Active" : "Completed",
        })}
      </Text>
      <Badge size="xs" variant="light" color={config.color} circle>
        {count}
      </Badge>
    </Group>
  );
};

const StatusSectionHeader = React.memo(StatusSectionHeaderComponent);

// =============================================================================
// Main Component
// =============================================================================

const ParkingSessionListComponent: React.FC<ParkingSessionListProps> = ({
  sessions: sessionsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditSession,
  onDeleteSession,
  onEndSession,
  onSessionClick,
  deletingSessionId: deletingSessionIdProp,
  endingSessionId,
  highlightedSessionId,
  onAddSession,
  grouped = true,
  formatCurrency,
}) => {
  const { t } = useTranslation(["parkingSessions", "common"]);

  // ── Context vs Props resolution ──
  // Props take priority so callers can override context values.
  const parkingContext = useOptionalParkingSessionsContext();

  const sessions = sessionsProp ?? parkingContext?.sessions ?? [];
  const loading = loadingProp ?? parkingContext?.isLoading ?? false;
  const loadingMore = loadingMoreProp ?? parkingContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? parkingContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? parkingContext?.loadMore;
  const deletingSessionId = deletingSessionIdProp ?? parkingContext?.removingId ?? null;

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } =
    useSwipeableList();

  // Entrance animation — fires once when data first arrives
  const { hasAnimated } = useEntranceAnimation({
    itemCount: sessions.length,
    staggerMs: 60,
  });

  const handleSwipeStateChange = useCallback(
    (sessionId: string, isOpen: boolean) => {
      if (isOpen) openCard(sessionId);
    },
    [openCard]
  );

  // Wrap action callbacks to close swiped card first (matching GloveboxList pattern)
  const handleEdit = useCallback(
    (session: ParkingSession) => {
      closeAllCards();
      onEditSession?.(session);
    },
    [closeAllCards, onEditSession]
  );

  const handleDelete = useCallback(
    (session: ParkingSession) => {
      closeAllCards();
      onDeleteSession?.(session);
    },
    [closeAllCards, onDeleteSession]
  );

  const handleEnd = useCallback(
    (session: ParkingSession) => {
      closeAllCards();
      onEndSession?.(session);
    },
    [closeAllCards, onEndSession]
  );

  // ==========================================
  // Group sessions by status
  // ==========================================

  const groupedSessions = useMemo(() => {
    const groups: Record<number, ParkingSession[]> = {
      [PARKING_SESSION_STATUS.ACTIVE]: [],
      [PARKING_SESSION_STATUS.COMPLETED]: [],
    };

    for (const session of sessions) {
      const status = session.status || PARKING_SESSION_STATUS.COMPLETED;
      if (groups[status]) {
        groups[status].push(session);
      } else {
        groups[PARKING_SESSION_STATUS.COMPLETED].push(session);
      }
    }

    return groups;
  }, [sessions]);

  // ==========================================
  // Render helpers
  // ==========================================

  // Render a single session card
  const renderCard = useCallback(
    (session: ParkingSession, isMobile: boolean) => (
      <motion.div
        key={session.id}
        variants={hasAnimated ? undefined : entranceCardVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        exit="exit"
        layout
      >
        <ParkingSessionCard
          session={session}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onEnd={handleEnd}
          //onClick={onSessionClick}
          highlighted={session.id === highlightedSessionId}
          isDeleting={deletingSessionId === session.id}
          isEnding={endingSessionId === session.id}
          skipAnimation={hasAnimated}
          formatCurrency={formatCurrency}
          swipeable={isMobile}
          forceClose={
            isMobile ? shouldForceClose(session.id || "") : undefined
          }
          onForceCloseComplete={
            isMobile
              ? () => handleForceCloseComplete(session.id || "")
              : undefined
          }
          onSwipeStateChange={
            isMobile
              ? (isOpen) =>
                handleSwipeStateChange(session.id || "", isOpen)
              : undefined
          }
        />
      </motion.div>
    ),
    [
      onSessionClick,
      handleEdit,
      handleDelete,
      handleEnd,
      highlightedSessionId,
      deletingSessionId,
      endingSessionId,
      formatCurrency,
      hasAnimated,
      shouldForceClose,
      handleForceCloseComplete,
      handleSwipeStateChange,
    ]
  );

  // Render a status section (grouped view)
  const renderSection = useCallback(
    (
      status: number,
      sectionSessions: ParkingSession[],
      isMobile: boolean
    ) => {
      if (sectionSessions.length === 0) return null;

      return (
        <Stack gap="sm" key={status}>
          <StatusSectionHeader
            status={status}
            count={sectionSessions.length}
          />
          <AnimatePresence mode="sync">
            {sectionSessions.map((session) =>
              renderCard(session, isMobile)
            )}
          </AnimatePresence>
        </Stack>
      );
    },
    [renderCard]
  );

  // Render grouped or flat content
  const renderContent = useCallback(
    (isMobile: boolean) => (
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-lg)",
        }}
      >
        {grouped ? (
          STATUS_GROUP_ORDER.map((status) =>
            renderSection(
              status,
              groupedSessions[status] || [],
              isMobile
            )
          )
        ) : (
          <AnimatePresence mode="sync">
            {sessions.map((session) => renderCard(session, isMobile))}
          </AnimatePresence>
        )}
      </motion.div>
    ),
    [
      hasAnimated,
      grouped,
      groupedSessions,
      sessions,
      renderSection,
      renderCard,
    ]
  );

  // ==========================================
  // Loading state
  // ==========================================

  if (loading && sessions.length === 0) {
    return <LoadingSkeleton />;
  }

  // ==========================================
  // Empty state
  // ==========================================

  if (!loading && sessions.length === 0) {
    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("parkingSessions:emptyState.title", {
            defaultValue: "No Parking Sessions",
          })}
          description={t("parkingSessions:emptyState.description", {
            defaultValue:
              "You haven't recorded any parking sessions yet. Start tracking your parking to see history and stats here.",
          })}
          iconName="Parking"
          actionLabel={t("parkingSessions:emptyState.actionLabel", {
            defaultValue: "Start Parking",
          })}
          onAction={onAddSession}
          size="md"
        />
      </Paper>
    );
  }

  // ==========================================
  // Render
  // ==========================================

  return (
    <Stack gap="md">
      {/* Mobile: swipeable cards */}
      <Box hiddenFrom="md">
        {renderContent(true)}
        <LoadMoreButton
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      </Box>

      {/* Desktop: full-width cards with hover actions */}
      <Box visibleFrom="md">
        {renderContent(false)}
        <LoadMoreButton
          hasMore={hasMore}
          loading={loading}
          loadingMore={loadingMore}
          onLoadMore={onLoadMore}
        />
      </Box>
    </Stack>
  );
};

export const ParkingSessionList = React.memo(ParkingSessionListComponent);